import {
  createContext,
  PropsWithChildren,
  startTransition,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';

import { onAuthStateChanged, type User as FirebaseUser } from 'firebase/auth';
import { get, ref } from 'firebase/database';

import { initializeFrpFirebase } from '../../lib/firebase-client';
import type { RuntimeEnvironment } from '../../lib/runtime-config';

// ── Types ─────────────────────────────────────────────────────────

export interface FrpUserSummary {
  uid: string;
  email: string | null;
  displayName: string | null;
}

export interface FrpLinkedOrg {
  id: string;
  name: string;
}

export interface FrpProfile {
  displayName?: string;
  email?: string;
  shopName?: string;
  shopMake?: string;
  linkedOrgId?: string;
  linkedOrgName?: string;
  autoShareToOrg?: boolean;
  settings?: {
    linkedOrgId?: string | null;
    autoShareToOrg?: boolean;
  };
  activeOrg?: {
    id?: string;
    name?: string;
    role?: string;
    joinedAt?: string;
  } | null;
  hourlyRate?: number | null;
  payType?: string;
  // Legacy stores as nested { type: '...' }, React stores as flat string — both supported
  paySchedule?: string | { type?: string; [key: string]: unknown };
  goals?: {
    dailyHours?: number | null;
    weeklyHours?: number | null;
    monthlyHours?: number | null;
    /** Weekly earnings target in $ (matches v3 goals.earningsTarget) */
    earningsTarget?: number | null;
    /** Speed / efficiency goal in % (50–100, matches v3 goals.efficiencyTarget) */
    efficiencyTarget?: number | null;
  };
  /** Set by org admin — when true the org manages this tech's profile/goals */
  managedByOrg?: boolean;
  /** Org ID of the managing organization */
  managedOrgId?: string;
  setupComplete?: boolean;
}

type SessionStatus = 'loading' | 'authenticated' | 'unauthenticated' | 'error';

interface FrpSessionContextValue {
  environment: RuntimeEnvironment;
  status: SessionStatus;
  user: FrpUserSummary | null;
  profile: FrpProfile | null;
  linkedOrg: FrpLinkedOrg | null;
  error: string | null;
  /** Reload profile from RTDB — call after profile mutations */
  refreshProfile: () => Promise<void>;
  /** Get a fresh Firebase ID token for the current user (used for API calls) */
  getIdToken: () => Promise<string>;
}

// ── Default state ─────────────────────────────────────────────────

const defaultSessionState: FrpSessionContextValue = {
  environment: 'development',
  status: 'loading',
  user: null,
  profile: null,
  linkedOrg: null,
  error: null,
  refreshProfile: async () => {},
  getIdToken: async () => { throw new Error('Not authenticated.'); }
};

// ── Context ───────────────────────────────────────────────────────

const FrpSessionContext = createContext<FrpSessionContextValue>(defaultSessionState);

function normalizeProfile(raw: FrpProfile | Record<string, unknown> | null | undefined): FrpProfile {
  const value = (raw || {}) as FrpProfile;
  const settings = value.settings || {};
  const activeOrg = value.activeOrg || null;
  const linkedOrgId = value.linkedOrgId || settings.linkedOrgId || activeOrg?.id || undefined;
  const linkedOrgName = value.linkedOrgName || activeOrg?.name || linkedOrgId || undefined;
  const autoShareToOrg = typeof value.autoShareToOrg === 'boolean'
    ? value.autoShareToOrg
    : Boolean(settings.autoShareToOrg);

  return {
    ...value,
    settings,
    activeOrg,
    linkedOrgId,
    linkedOrgName,
    autoShareToOrg
  };
}

// ── Provider ──────────────────────────────────────────────────────

export function FrpSessionProvider({ children }: PropsWithChildren) {
  const [session, setSession] = useState<FrpSessionContextValue>(defaultSessionState);
  const firebaseUserRef = useRef<FirebaseUser | null>(null);

  useEffect(() => {
    let disposed = false;
    let unsubscribeAuth: (() => void) | null = null;

    async function bootstrap() {
      try {
        const firebaseClient = await initializeFrpFirebase();
        if (disposed) {
          return;
        }

        startTransition(() => {
          setSession((current) => ({
            ...current,
            environment: firebaseClient.environment
          }));
        });

        unsubscribeAuth = onAuthStateChanged(firebaseClient.auth, async (firebaseUser) => {
          if (disposed) {
            return;
          }
          firebaseUserRef.current = firebaseUser;

          if (!firebaseUser) {
            startTransition(() => {
              setSession((current) => ({
                ...current,
                status: 'unauthenticated',
                user: null,
                profile: null,
                linkedOrg: null,
                error: null
              }));
            });
            return;
          }

          const userSummary: FrpUserSummary = {
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName
          };

          try {
            await firebaseUser.getIdToken();
            const profileSnap = await get(ref(firebaseClient.db, `users/${firebaseUser.uid}/profile`));
            const profile = normalizeProfile(profileSnap.exists() ? (profileSnap.val() as FrpProfile) : {});

            let linkedOrg: FrpLinkedOrg | null = null;
            if (profile.linkedOrgId) {
              linkedOrg = {
                id: profile.linkedOrgId,
                name: profile.linkedOrgName || profile.linkedOrgId
              };
            }

            if (disposed) {
              return;
            }

            startTransition(() => {
              setSession((current) => ({
                ...current,
                status: 'authenticated',
                user: userSummary,
                profile,
                linkedOrg,
                error: null
              }));
            });
          } catch (profileError) {
            if (disposed) {
              return;
            }

            const profileErrorMessage = profileError instanceof Error
              ? profileError.message
              : 'Failed to load profile.';

            startTransition(() => {
              setSession((current) => ({
                ...current,
                status: 'authenticated',
                user: userSummary,
                profile: null,
                linkedOrg: null,
                error: profileErrorMessage
              }));
            });
          }
        });
      } catch (err) {
        if (!disposed) {
          startTransition(() => {
            setSession((current) => ({
              ...current,
              status: 'error',
              error: err instanceof Error ? err.message : 'Failed to initialize.'
            }));
          });
        }
      }
    }

    void bootstrap();

    return () => {
      disposed = true;
      unsubscribeAuth?.();
    };
  }, []);

  // Provide a refresh function so screens can reload profile after mutations
  const refreshProfile = async () => {
    const uid = session.user?.uid ?? firebaseUserRef.current?.uid;
    if (!uid) {
      return;
    }
    try {
      const firebaseClient = await initializeFrpFirebase();
      if (firebaseUserRef.current) {
        await firebaseUserRef.current.getIdToken();
      }
      const profileSnap = await get(ref(firebaseClient.db, `users/${uid}/profile`));
      const profile = normalizeProfile(profileSnap.exists() ? (profileSnap.val() as FrpProfile) : {});
      const linkedOrg: FrpLinkedOrg | null = profile.linkedOrgId
        ? { id: profile.linkedOrgId, name: profile.linkedOrgName || profile.linkedOrgId }
        : null;

      startTransition(() => {
        setSession((current) => ({ ...current, profile, linkedOrg }));
      });
    } catch {
      // Non-fatal — silently ignore refresh failures
    }
  };

  const getIdToken = async (): Promise<string> => {
    if (!firebaseUserRef.current) throw new Error('Not authenticated.');
    return firebaseUserRef.current.getIdToken();
  };

  const value: FrpSessionContextValue = { ...session, refreshProfile, getIdToken };

  return (
    <FrpSessionContext.Provider value={value}>
      {children}
    </FrpSessionContext.Provider>
  );
}

// ── Hook ──────────────────────────────────────────────────────────

export function useFrpSession(): FrpSessionContextValue {
  return useContext(FrpSessionContext);
}
